import React, { useState, useEffect } from 'react';
import { adminApi } from '../api/adminApi';
import toast, { Toaster } from 'react-hot-toast';
import PremiumTable from '../components/common/PremiumTable';

const Staff = () => {
  const [staffList, setStaffList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('staff'); // 'staff' or 'employees'

  useEffect(() => {
    fetchData();
  }, [view]);

  const fetchData = async () => {
    try {
      setLoading(true);
      if (view === 'staff') {
        const res = await adminApi.getAllStaff();
        setStaffList(res.data.data);
      } else {
        const res = await adminApi.getAllEmployees();
        setEmployees(res.data.data);
      }
    } catch (error) {
      console.error(`Error fetching ${view}:`, error);
      toast.error(`Failed to load ${view}`);
    } finally {
      setLoading(false);
    }
  };

  const statusBadge = (isActive) => {
    return isActive 
      ? <span className="px-3 py-1 inline-flex text-xs font-bold rounded-full bg-green-100 text-green-800 border border-green-200">Active</span>
      : <span className="px-3 py-1 inline-flex text-xs font-bold rounded-full bg-red-100 text-red-800 border border-red-200">Inactive</span>;
  };

  const columns = [
    {
      header: 'Name',
      accessor: 'name',
      render: (row) => (
        <div>
          <div className="text-sm font-bold text-gray-900">{row.name}</div>
          <div className="text-xs text-gray-500">{row.phone || 'N/A'}</div>
        </div>
      )
    },
    {
      header: 'Business',
      accessor: 'businessId.name',
      render: (row) => (
        <span className="text-sm text-gray-900">{row.businessId?.name || 'Unknown'}</span>
      )
    },
    {
      header: 'Role',
      accessor: 'role',
      render: (row) => (
        <span className="text-sm text-gray-500 capitalize">{row.role}</span>
      )
    },
    {
      header: view === 'employees' ? 'Salary' : 'App User',
      accessor: view === 'employees' ? 'salaryAmount' : 'user.email',
      render: (row) => (
        view === 'employees' ? (
          <div className="text-sm font-medium text-gray-900">
            ₹{row.salaryAmount} / {row.salaryType}
          </div>
        ) : (
          <div className="text-sm text-gray-500">
            {row.user?.email || 'N/A'}
          </div>
        )
      )
    },
    {
      header: 'Status',
      accessor: view === 'staff' ? 'isActive' : 'status',
      render: (row) => statusBadge(view === 'staff' ? row.isActive : row.status === 'active')
    }
  ];

  return (
    <div className="min-h-screen bg-transparent pb-12">
      <Toaster position="top-right" />
      
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff & Employee Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage app staff and payroll employees.</p>
        </div>
        <div className="flex space-x-2 bg-gray-100/80 p-1 rounded-full ring-1 ring-gray-200 shadow-inner">
          <button
            onClick={() => setView('staff')}
            className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${
              view === 'staff' 
                ? 'bg-white shadow-md text-red-600 scale-105' 
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            App Staff
          </button>
          <button
            onClick={() => setView('employees')}
            className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${
              view === 'employees' 
                ? 'bg-white shadow-md text-red-600 scale-105' 
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            Payroll Employees
          </button>
        </div>
      </div>

      <main className="max-w-7xl mx-auto">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading {view}...</div>
        ) : (
          <PremiumTable 
            title={view === 'staff' ? 'App Staff Records' : 'Payroll Employee Records'}
            data={view === 'staff' ? staffList : employees}
            columns={columns}
            searchKeys={['name', 'phone', 'businessId.name', 'role']}
            primaryKey="_id"
            paginationMode="standard"
            itemsPerPage={10}
          />
        )}
      </main>
    </div>
  );
};

export default Staff;
